import type { RumEvent } from "@datadog/browser-rum";

/**
 * Known-benign RUM errors that add noise to Error Tracking without signal.
 * They are dropped in RUM's `beforeSend` so genuine, actionable errors aren't
 * buried under thousands of these per day.
 *
 * - "Object Not Found Matching Id..."; emitted by Outlook/Office SafeLink
 *   link-scanning crawlers (they run a headless browser over emailed links),
 *   not by real users. Dominates the error volume on auth pages like /login.
 * - "[GSI_LOGGER] ... FedCM get() rejects ..."; Google One Tap / FedCM
 *   rejections that are expected when the visitor has no active Google session
 *   or dismisses the prompt; not a fault in our code.
 * - "Failed to load Google Identity Services script"; the One Tap script was
 *   blocked (typically by an ad/tracking blocker); non-actionable.
 *
 * Keep this list tight and specific: each pattern must match only clearly
 * non-actionable noise, never a class of error that could hide a real bug.
 */
const BENIGN_ERROR_PATTERNS: readonly RegExp[] = [
  /Object Not Found Matching Id/i,
  /\[GSI_LOGGER\]/i,
  /FedCM get\(\) (?:rejects|was aborted)/i,
  /Failed to load Google Identity Services script/i,
];

/** True when `message` matches a known non-actionable RUM error. */
export function isBenignRumError(message: string | undefined | null): boolean {
  if (!message) {
    return false;
  }
  return BENIGN_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * RUM `beforeSend` predicate. Returns `false` to discard the event. Only error
 * events are inspected; every other event type (view, action, resource,
 * long_task, vital) passes through untouched.
 */
export function shouldSendRumEvent(event: RumEvent): boolean {
  if (event.type === "error") {
    return !isBenignRumError(event.error.message);
  }
  return true;
}
